import { useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";

/**
 * Manual per-section "Not Applicable" (s85, D-84.8).
 *
 * Fetches the planner's manually-N/A'd section slugs and exposes mark / bring-back
 * mutations. A marked section renders the tucked-away SectionNotApplicable state
 * (no nag, no green check) and drops out of NEXT UP — a parallel path to the
 * auto-NA situation-flags gates, for OPTIONAL non-gating sections only (eligibility
 * is enforced by MANUAL_NA_ELIGIBLE_SECTIONS on both client and server).
 *
 * The query key matches `useDashboardProgress`'s section-na query, so invalidating
 * here also refreshes the dashboard pointer (TanStack dedupes the fetch).
 */
export function useClientSectionNa(clientId: string | undefined) {
  const key = ["/api/clients", clientId, "section-na"];

  const { data, isSuccess } = useQuery<string[]>({
    queryKey: key,
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/section-na`);
      if (!res.ok) throw new Error("Failed to load N/A sections");
      return res.json();
    },
    enabled: !!clientId,
    staleTime: 0,
  });

  const naSlugs = useMemo(() => new Set(data ?? []), [data]);

  // s87 (Bundle A): marking a section N/A propagates into the ERS (s86 — manualNaSlugs
  // flows into the evaluator), so the in-section toggle must also invalidate the
  // estate-ready-score query, else the dashboard essentials card reads stale until a
  // refetch. Mirrors NextUpCard's dashboard skip path, which already invalidates both.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "section-na"] });
    queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "estate-ready-score"] });
  };

  const markNa = useMutation({
    mutationFn: async (sectionSlug: string) => {
      const res = await apiRequest("POST", `/api/clients/${clientId}/section-na`, { sectionSlug });
      if (!res.ok) throw new Error((await res.json()).message || "Failed to mark N/A");
      return res.json();
    },
    onSuccess: invalidate,
  });

  const bringBack = useMutation({
    mutationFn: async (sectionSlug: string) => {
      const res = await apiRequest(
        "DELETE",
        `/api/clients/${clientId}/section-na/${encodeURIComponent(sectionSlug)}`,
      );
      if (!res.ok && res.status !== 404) {
        throw new Error((await res.json()).message || "Failed to bring back");
      }
      return true;
    },
    onSuccess: invalidate,
  });

  return {
    naSlugs,
    // s237 (chapter three PR 2): the list has arrived — a screen that seeds its answer once waits for it (D-237.26).
    loaded: isSuccess,
    markNa: (slug: string) => markNa.mutate(slug),
    bringBack: (slug: string) => bringBack.mutate(slug),
    isPending: markNa.isPending || bringBack.isPending,
  };
}
